import { isTauri } from '@heidi/runtime/platform';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import {
  dictateOnboardingDevLayoutOverrideAtom,
  getDictateOnboardingDevLayoutOverride,
  isDictateOnboardingDevOverridesEnabled,
} from './dictateOnboardingDevAtoms';
import { getDictationKeyboardPorts } from './keyboard-ports';
import { type KeyboardLayoutMap } from './layoutDisplayLabel';

export type KeyboardLayout =
  | 'standard'
  | 'france-belgium'
  | 'germany-austria'
  | 'switzerland'
  | 'spain'
  | 'italy'
  | 'uk'
  | 'japan';

const DEFAULT_LAYOUT: KeyboardLayout = 'standard';

/**
 * Cached resolved keyboard layout — populated by `useKeyboardLayout`'s effect.
 * Lets non-React code (e.g. the dictation zustand slice via `getDictationHotkey`)
 * read the layout synchronously. Layout is essentially stable for a session, so
 * a single late-write cache is fine. Bootstrap window: before the first effect
 * resolves, reads return `'standard'`.
 */
export const keyboardLayoutAtom = atom<KeyboardLayout>(DEFAULT_LAYOUT);

/** Non-hook accessor for the cached keyboard layout (respects dev layout override). */
export const getKeyboardLayout = (): KeyboardLayout => {
  const devOverride = getDictateOnboardingDevLayoutOverride();
  if (devOverride != null) {
    return devOverride;
  }
  return getDefaultStore().get(keyboardLayoutAtom);
};

interface KeyboardWithLayoutMap {
  getLayoutMap(): Promise<Map<string, string>>;
}

const hasKeyboardLayoutMap = (
  nav: Navigator
): nav is Navigator & { keyboard: KeyboardWithLayoutMap } =>
  'keyboard' in nav &&
  nav.keyboard != null &&
  typeof (nav.keyboard as KeyboardWithLayoutMap).getLayoutMap === 'function';

/**
 * Pure resolver for the web-fallback path — exported for unit testing.
 *
 * Discriminator order matters: Japan must come before AZERTY (JIS still uses
 * QWERTY letter positions but adds `IntlYen`/`KanaMode`), and the QWERTZ branch
 * must come before the Spanish Backquote check (German `Backquote` is `^`,
 * Swiss `Backquote` is `§` — neither matches the Spanish `º`/`ª`).
 */
export const resolveLayoutFromMap = (layoutMap: Map<string, string>): KeyboardLayout => {
  // JIS-only signal. macOS Chrome includes `IntlYen` (and sometimes a Yen
  // mapping) in the layout map for non-JIS keyboards too — e.g. "U.S.
  // Extended" / "ABC – Extended". Require BOTH the IntlYen → `¥` map AND a
  // Japanese locale before classifying as Japan, so phantom-key configs on
  // US Macs don't false-positive.
  const intlYen = layoutMap.get('IntlYen');
  const isJapaneseLocale =
    typeof navigator !== 'undefined' &&
    navigator.language?.toLowerCase().startsWith('ja');
  if (intlYen === '¥' && isJapaneseLocale) return 'japan';
  if (layoutMap.get('KeyQ') === 'a') return 'france-belgium';
  if (layoutMap.get('KeyY') === 'z' && layoutMap.get('KeyZ') === 'y') {
    return layoutMap.get('Backquote') === '§' ? 'switzerland' : 'germany-austria';
  }
  const bq = layoutMap.get('Backquote');
  if (bq === 'º' || bq === 'ª') return 'spain';
  // Italian QWERTY produces `\` on the Backquote position. Latin-American
  // Spanish also produces `\` here — accepted false positive (no Latam asset).
  if (bq === '\\') return 'italy';
  // UK ISO QWERTY has an extra physical key (IntlBackslash) that US ANSI lacks.
  // Discriminator: presence of IntlBackslash plus a `\`` from Backquote.
  if (layoutMap.has('IntlBackslash') && bq === '`') return 'uk';
  return DEFAULT_LAYOUT;
};

/**
 * Pure resolver for the Tauri path — maps the raw OS layout identifier
 * returned by the `get_keyboard_layout` IPC command to a `KeyboardLayout`.
 *
 * - macOS layout IDs look like `com.apple.keylayout.French` (for keyboard
 *   layouts) or `com.apple.inputmethod.Kotoeri.RomajiTyping.Japanese` (for
 *   IMEs). String-substring matching is the most resilient approach because
 *   Apple ships several locale variants per region (e.g. `French-PC`,
 *   `SwissGerman`, `Spanish-ISO`).
 * - Windows KLIDs are 8-char hex (`GetKeyboardLayoutNameW`); the low 16 bits are
 *   the LANGID and the high 16 bits flag variants (e.g. `00010407` German IBM).
 *   We match on the LANGID so variants still resolve to their region.
 *
 * Latin-American Spanish intentionally maps to `standard` — there is no
 * dedicated asset for that layout.
 */
export const resolveLayoutFromId = (layoutId: string): KeyboardLayout => {
  if (layoutId.startsWith('com.apple.')) {
    if (/Kotoeri|Japanese|Kana|Romaji/i.test(layoutId)) return 'japan';
    if (/Belgian|French/i.test(layoutId)) {
      return /Swiss/i.test(layoutId) ? 'switzerland' : 'france-belgium';
    }
    if (/Austrian|German/i.test(layoutId)) {
      return /Swiss/i.test(layoutId) ? 'switzerland' : 'germany-austria';
    }
    if (/Spanish/i.test(layoutId) && !/LatinAmerican/i.test(layoutId)) return 'spain';
    if (/Italian/i.test(layoutId)) return 'italy';
    if (/British|Irish/i.test(layoutId)) return 'uk';
    // Non-regional physical-layout variants (e.g. `ABC – QWERTZ`,
    // `ABC – AZERTY`, `USInternational-PC`) carry no country name. Fall back to
    // the physical key arrangement so they still pick the right illustration.
    if (/AZERTY/i.test(layoutId)) return 'france-belgium';
    if (/QWERTZ/i.test(layoutId)) return 'germany-austria';
    return DEFAULT_LAYOUT;
  }
  // Match on the low-word LANGID (last 4 hex digits) so variant/custom layouts
  // that share a base language — e.g. `00010407` (German IBM), `0001080c`
  // (Belgian Comma) — still resolve instead of falling back to `standard`.
  switch (layoutId.toLowerCase().slice(-4)) {
    case '0411':
      return 'japan';
    case '040c': // French (France)
    case '080c': // French (Belgium)
      return 'france-belgium';
    case '0407': // German (Germany)
    case '0c07': // German (Austria)
      return 'germany-austria';
    case '0807': // German (Switzerland)
    case '100c': // French (Switzerland)
      return 'switzerland';
    case '040a': // Spanish (Spain, traditional sort)
    case '0c0a': // Spanish (Spain, modern sort)
      return 'spain';
    case '0410': // Italian
      return 'italy';
    case '0809': // English (United Kingdom)
    case '1809': // English (Ireland)
      return 'uk';
    default:
      return DEFAULT_LAYOUT;
  }
};

/**
 * Detects the user's keyboard layout (used to pick the correct onboarding
 * illustration and dictation hotkey default).
 *
 * On the Tauri desktop app, calls the native `get_keyboard_layout` IPC command
 * which reads the OS-level identifier (Carbon TIS on macOS, GetKeyboardLayoutNameW
 * on Windows). Falls back to `navigator.keyboard.getLayoutMap()` on web and on
 * any IPC failure. Defaults to `'standard'` when neither path resolves. Guards
 * against `setState`-after-unmount with an `isCancelled` flag in the cleanup.
 */
export const useKeyboardLayout = (): {
  layout: KeyboardLayout;
  layoutMap: KeyboardLayoutMap | null;
} => {
  const layoutOverride = useAtomValue(dictateOnboardingDevLayoutOverrideAtom);
  const devLayoutOverride = isDictateOnboardingDevOverridesEnabled()
    ? layoutOverride
    : null;

  const [layout, setLayout] = useState<KeyboardLayout>(() => getKeyboardLayout());
  const [layoutMap, setLayoutMap] = useState<KeyboardLayoutMap | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const applyLayout = (resolved: KeyboardLayout) => {
      setLayout(resolved);
      getDefaultStore().set(keyboardLayoutAtom, resolved);
    };

    if (devLayoutOverride != null) {
      applyLayout(devLayoutOverride);
      return () => {
        isCancelled = true;
      };
    }

    // Drops overlapping detections: regaining focus fires both `focus` and
    // `visibilitychange`, and the in-flight run already reads the current
    // layout, so the duplicate native IPC is safe to skip.
    let isDetecting = false;

    const detectLayout = async () => {
      if (isDetecting) return;
      isDetecting = true;
      try {
        // Fetch the raw glyph map once (Chromium webview: web + Windows
        // WebView2). It powers AZERTY/QWERTZ glyph substitution regardless of
        // how the layout is classified, and doubles as the web classification
        // signal so we never fetch `getLayoutMap()` twice. macOS WKWebView
        // lacks the API → stays null and glyph display falls back to legends.
        let rawMap: Map<string, string> | null = null;
        if (hasKeyboardLayoutMap(navigator)) {
          try {
            const map = await navigator.keyboard.getLayoutMap();
            if (isCancelled) return;
            rawMap = map;
            setLayoutMap(map);
          } catch {
            // Glyph substitution falls back to formatChordTokens / legends.
          }
        }

        let fromId: KeyboardLayout = DEFAULT_LAYOUT;
        try {
          if (isTauri()) {
            const layoutId = await getDictationKeyboardPorts().getKeyboardLayout();
            if (isCancelled) return;
            if (layoutId) {
              fromId = resolveLayoutFromId(layoutId);
              // macOS IDs are authoritative; an unrecognized Windows KLID is
              // inconclusive (it names the language, not the layout), so fall
              // through to the probe (e.g. Wolof `0488` is AZERTY).
              const isMacLayoutId = layoutId.startsWith('com.apple.');
              if (fromId !== DEFAULT_LAYOUT || isMacLayoutId) {
                applyLayout(fromId);
                return;
              }
            }
          }
        } catch {
          // Fall through to the web classification.
        }

        applyLayout(rawMap ? resolveLayoutFromMap(rawMap) : fromId);
      } finally {
        isDetecting = false;
      }
    };

    void detectLayout();

    // Live re-sync: the native command reads the current active input source,
    // so re-detect on focus / visibility (the user may have switched layout
    // elsewhere). Re-runs are idempotent — an unchanged layout is a no-op.
    const handleReSync = () => {
      if (document.visibilityState === 'visible') {
        void detectLayout();
      }
    };
    document.addEventListener('visibilitychange', handleReSync);
    window.addEventListener('focus', handleReSync);

    return () => {
      isCancelled = true;
      document.removeEventListener('visibilitychange', handleReSync);
      window.removeEventListener('focus', handleReSync);
    };
  }, [devLayoutOverride]);

  return { layout, layoutMap };
};
